import { FileUser, ScrollText } from "lucide-react";

import type { Contract } from "@/types/contract";

import { useContract } from "@/api/contract-query";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { formatCurrency, formatDate } from "@/lib/utils";
import { Link } from "@tanstack/react-router";

import { ShareButton } from "./share-btn";

interface ContractDialogProps {
	contract: Contract;
}

export default function ContractDialog({ contract: data }: ContractDialogProps) {
	const genTime = (date: string) => {
		const time = (new Date(date)).toLocaleTimeString("en-GB", {
			hour: "2-digit",
			minute: "2-digit"
		});

		const formattedDate = formatDate(date);
		return `${time} | ${formattedDate}`;
	};

	const { data: contract, isPending, error } = useContract({ contractId: data.id });
	const totalContactAmount = contract?.contractItem.reduce((acc, item) => acc + item.totalCost, 0) || 0;

	if (isPending) {
		return (
			<div className="flex flex-col items-center justify-center gap-4">
				<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
				<p className="text-muted-foreground">Loading product details...</p>
			</div>
		);
	}
	if (error) {
		return (
			<div className="flex flex-col items-center justify-center gap-4">
				<p className="text-muted-foreground">Error fetching contract. Please try again later</p>
			</div>
		);
	}
	return (
		<Dialog>
			<DialogTrigger className="flex flex-col items-center gap-2" asChild>
				<Button size="icon" className="rounded-full items-center justify-center p-2">
					<FileUser />
				</Button>
			</DialogTrigger>
			<DialogContent className="max-w-xl">
				<div className="pt-6">
					<div className="flex flex-col items-center mb-8">
						<div className="h-16 w-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
							<ScrollText className="h-8 w-8 text-primary" />
						</div>
						<h1 className="text-sm font-medium mb-1">Total contract amount</h1>
						<div className="text-3xl font-bold mb-2">{formatCurrency(totalContactAmount, "NGN")}</div>
					</div>

					<div className="flex justify-center gap-8 text-sm text-gray-500 mb-6">
						<span>
							{genTime(contract.createdAt)}
						</span>
						<span>{`Contract ID: #${contract.contractCode}`}</span>
					</div>

					<div className="border rounded-lg mb-6">
						<Table>
							<TableBody>
								{contract.contractItem.map(item => (
									<TableRow key={item.id}>
										<TableCell className="flex items-center">
											{item.productId.name}
											<span className="text-muted-foreground text-xs ml-2">
												x
												{item.productQuantity}
											</span>
										</TableCell>
										<TableCell className="text-right">{formatCurrency(item.totalCost, "NGN")}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>

					<div className="border rounded-lg">
						<Table>
							<TableBody>
								<TableRow>
									<TableCell>Contract name</TableCell>
									<TableCell className="text-right">{contract?.title}</TableCell>
								</TableRow>
								{/* <TableRow>
									<TableCell>Vendor</TableCell>
									<TableCell className="text-right">Okonkwo Bella</TableCell>
								</TableRow> */}
								<TableRow>
									<TableCell>Amount</TableCell>
									<TableCell className="text-right">{formatCurrency(totalContactAmount, "NGN")}</TableCell>
								</TableRow>
								<TableRow>
									<TableCell>Shipping out date</TableCell>
									<TableCell className="text-right">{formatDate(contract.shippingDate)}</TableCell>
								</TableRow>
								<TableRow>
									<TableCell>Estimated delivery date</TableCell>
									<TableCell className="text-right">{formatDate(contract.estimatedDeliveryDate)}</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</div>
				</div>

				<DialogFooter className="flex justify-between gap-4 mt-6">
					<Button variant="outline" className="w-full" asChild>
						<Link to="/vendor/home">Back to home</Link>
					</Button>
					{/* <Button className="w-full">
						Share contract
					</Button> */}
					<ShareButton contractCode={contract.contractCode} contractName={contract.title} />
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
